import { useMemo, type ReactNode } from "react"
import { Badge } from "@ui/shad"

import { useFetchTeams } from "@/hooks/api/teams/queries"
import { useFetchUsers } from "@/hooks/api/users"

type PrivacyViewerListProps = {
  creatorId?: string | null
  assigneeId?: string | null
  teamId?: string | null
  collaboratorIds?: string[]
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">{children}</div>
}

function NameBadge({ name }: { name: string }) {
  return (
    <Badge variant="secondary" size="sm" className="max-w-full truncate font-normal">
      {name}
    </Badge>
  )
}

export function PrivacyViewerList({ creatorId, assigneeId, teamId, collaboratorIds = [] }: PrivacyViewerListProps) {
  const { data: usersData } = useFetchUsers()
  const { data: teamsData } = useFetchTeams({ enabled: !!teamId })

  const usersById = useMemo(() => new Map((usersData?.users ?? []).map((u) => [u.id, u])), [usersData?.users])
  const team = useMemo(
    () => (teamId ? (teamsData?.teams?.find((t) => t.id === teamId) ?? null) : null),
    [teamId, teamsData?.teams]
  )

  const nameFor = (id: string): string => {
    const user = usersById.get(id)
    return user?.full_name || user?.email || "Unknown user"
  }

  const teamMemberIds = new Set((team?.users ?? []).map((u) => u.id))
  const seen = new Set<string>()

  const creatorName = creatorId ? nameFor(creatorId) : null
  if (creatorId) seen.add(creatorId)

  const assigneeName = assigneeId && !seen.has(assigneeId) ? nameFor(assigneeId) : null
  if (assigneeId) seen.add(assigneeId)

  const flatCollaborators = collaboratorIds
    .filter((id) => !seen.has(id) && !teamMemberIds.has(id))
    .map((id) => {
      seen.add(id)
      return { id, name: nameFor(id) }
    })

  const nestedTeamMembers = (team?.users ?? []).filter((u) => !seen.has(u.id))

  return (
    <div className="space-y-3">
      <div className="text-foreground text-xs font-semibold">Private — visible to:</div>

      {creatorName && (
        <div className="space-y-1">
          <SectionLabel>Creator</SectionLabel>
          <div className="flex flex-wrap gap-1">
            <NameBadge name={creatorName} />
          </div>
        </div>
      )}

      {assigneeName && (
        <div className="space-y-1">
          <SectionLabel>Assignee</SectionLabel>
          <div className="flex flex-wrap gap-1">
            <NameBadge name={assigneeName} />
          </div>
        </div>
      )}

      {team && (
        <div className="space-y-1">
          <SectionLabel>Team — {team.name}</SectionLabel>
          {nestedTeamMembers.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {nestedTeamMembers.map((m) => (
                <NameBadge key={m.id} name={m.full_name || m.email} />
              ))}
            </div>
          )}
        </div>
      )}

      {flatCollaborators.length > 0 && (
        <div className="space-y-1">
          <SectionLabel>Collaborators</SectionLabel>
          <div className="flex flex-wrap gap-1">
            {flatCollaborators.map((c) => (
              <NameBadge key={c.id} name={c.name} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
